<template>
  <div
    ref="container"
    class="donut-chart-container"
    style="height: 100%;"
  >
    <svg ref="mainSvg"><!----></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
    props: {
        data: {
            type: Array,
            required: true,
        },
        chartSize: {
            type: Number,
            default: 300,
        },
    },
    computed: {
        donutWidth() {
            if (this.$refs.container) {
                const containerWidth = this.$refs.container.clientWidth;
                const containerHeight = this.$refs.container.clientHeight;
                const count = this.data.length || 1;
                const spacing = 20;
                const availableWidthPerDonut = (containerWidth - spacing * (count - 1)) / count;
                return Math.min(availableWidthPerDonut, containerHeight);
            }
            return this.chartSize;
        },
        totalSvgWidth() {
            const spacing = 20;
            const count = this.data.length;
            return (this.donutWidth * count) + spacing * (count - 1);
        },
        totalSvgHeight() {
            const titleMargin = 30;
            const nameMargin = 30;
            return this.donutWidth + titleMargin + nameMargin;
        },
        processedData() {
            return this.data.map(series => {
                if (!series.data || series.data.length === 0) return [];
                const { norm } = series.data[series.data.length - 1].metadata;
                if (!norm || !(norm.minimum || norm.low || norm.high || norm.maximum)) return [];
                const counts = {
                    critical: 0,
                    warning: 0,
                    normal: 0,
                };
                series.data.forEach(({ value }) => {
                    if (value < norm.low) counts.critical += 1;
                    else if (value < norm.minimum) {
                        if (norm.low === null) counts.critical += 1;
                        else counts.warning += 1;
                    } else if (value < norm.maximum || norm.maximum === null) counts.normal += 1;
                    else if (value < norm.high) counts.warning += 1;
                    else counts.critical += 1;
                });
                return Object.entries(counts).map(([key, count]) => ({
                    label: this.$t(`graphics.${key}`),
                    value: count,
                }));
            });
        },
    },
    watch: {
        data: {
            handler() {
                this.renderAllDonuts();
            },
            deep: true,
        },
    },
    mounted() {
        this.renderAllDonuts();
        window.addEventListener('resize', this.renderAllDonuts);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.renderAllDonuts);
    },
    updated() {
        this.renderAllDonuts();
    },
    methods: {
        renderAllDonuts() {
            this.$nextTick(() => {
                const titleMargin = 30;
                const nameMargin = 25;
                const spacing = 20;
                const donutAreaHeight = this.donutWidth;
                const totalWidth = this.totalSvgWidth;
                const totalHeight = this.totalSvgHeight;

                const svg = d3.select(this.$refs.mainSvg)
                    .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
                    .style('width', '100%')
                    .style('height', '100%');

                svg.selectAll('*').remove();

                svg.append('text')
                    .attr('x', totalWidth / 2)
                    .attr('y', titleMargin * 0.8)
                    .attr('text-anchor', 'middle')
                    .style('font-size', '20px')
                    .style('font-weight', 'bold')
                    .style('fill', '#333')
                    .text(this.$t('graphics.donutChartTitle'));

                this.processedData.forEach((chartData, index) => {
                    const group = svg.append('g')
                        .attr('transform', `translate(${index * (this.donutWidth + spacing)}, ${titleMargin})`);

                    const centerX = this.donutWidth / 2;
                    const centerY = donutAreaHeight / 2;
                    const donutGroup = group.append('g')
                        .attr('transform', `translate(${centerX}, ${centerY})`);

                    const radius = Math.min(this.donutWidth, donutAreaHeight) / 2;

                    if (chartData.length === 0) {
                        donutGroup.append('text')
                            .attr('text-anchor', 'middle')
                            .attr('dy', '.35em')
                            .style('font-size', '14px')
                            .style('font-weight', 'bold')
                            .style('fill', 'red')
                            .text(this.$t('graphics.noNorm'));
                    } else {
                        const color = d3.scaleOrdinal()
                            .domain(chartData.map(d => d.label))
                            .range(['#b10a0a', '#ffc700', '#00c522']);

                        const pie = d3.pie().value(d => d.value);
                        const arc = d3.arc().innerRadius(radius * 0.6).outerRadius(radius);

                        let tooltip = d3.select('body').select('.donut-tooltip');
                        if (tooltip.empty()) {
                            tooltip = d3.select('body')
                                .append('div')
                                .attr('class', 'donut-tooltip')
                                .style('position', 'absolute')
                                .style('padding', '5px 10px')
                                .style('background', 'rgba(0, 0, 0, 0.7)')
                                .style('color', '#fff')
                                .style('border-radius', '3px')
                                .style('pointer-events', 'none')
                                .style('font-size', '12px')
                                .style('display', 'none');
                        }

                        const arcGroup = donutGroup.append('g').attr('class', 'arc-group');

                        arcGroup.selectAll('.arc')
                            .data(pie(chartData))
                            .enter()
                            .append('path')
                            .attr('class', 'arc')
                            .attr('d', arc)
                            .attr('fill', d => color(d.data.label))
                            .attr('stroke', 'white')
                            .style('stroke-width', '2px')
                            .on('mouseover', (event, d) => {
                                tooltip.style('display', 'block')
                                    .html(`${d.data.label}<br>${this.$t('graphics.count')}: ${d.data.value}`);
                            })
                            .on('mousemove', event => {
                                tooltip.style('left', `${event.pageX + 10}px`)
                                    .style('top', `${event.pageY + 10}px`);
                            })
                            .on('mouseout', () => {
                                tooltip.style('display', 'none');
                            });

                        arcGroup.transition()
                            .duration(1500)
                            .attrTween('transform', () => d3.interpolateString('rotate(0)', 'rotate(360)'));
                    }

                    const nameText = group.append('text')
                        .attr('x', centerX)
                        .attr('y', donutAreaHeight + nameMargin / 2)
                        .attr('dy', '0em')
                        .attr('text-anchor', 'middle')
                        .style('font-size', '12px')
                        .style('fill', '#333')
                        .text(this.data[index].name);
                    this.wrapText(nameText, this.donutWidth);
                });
            });
        },
        wrapText(textSelection, maxWidth) {
            textSelection.each(function wrapTextFunction() {
                const text = d3.select(this);
                const words = text.text().split(/\s+/).reverse();
                let word;
                let line = [];
                const lineHeight = 1;
                const x = text.attr('x');
                const y = text.attr('y');
                text.text(null);
                let tspan = text.append('tspan')
                    .attr('x', x)
                    .attr('y', y);
                while (words.length > 0) {
                    word = words.pop();
                    line.push(word);
                    tspan.text(line.join(' '));
                    if (tspan.node().getComputedTextLength() > maxWidth) {
                        line.pop();
                        tspan.text(line.join(' '));
                        line = [word];
                        tspan = text.append('tspan')
                            .attr('x', x)
                            .attr('y', y)
                            .attr('dy', `${lineHeight }em`)
                            .text(word);
                    }
                }
            });
        },
    },
};
</script>

  <style scoped>
  .donut-chart-container {
    width: 100%;
    text-align: center;
    overflow: hidden;
    height: 100%;
  }
  .donut-tooltip {
    z-index: 1000;
  }
  </style>
